import React, { useState, useRef } from "react";
import {
  Box, Flex, Heading, Spinner, useToast, Text, UnorderedList,
  ListItem, Button, useDisclosure, RadioGroup, Radio, Stack, Input, IconButton
} from "@chakra-ui/react";
import { useCognito } from "../utils/useCognito";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import BAAForm from "../components/BAAForm";
import NpiLookupDrawer from "../components/NpiLookupDrawer";
import { CopyIcon } from "@chakra-ui/icons";

const AdminBAACreator = () => {
  const { getIdToken } = useCognito();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get("email") || "");

  const [loading, setLoading] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [createdUserId, setCreatedUserId] = useState(null);
  const [updatedFields, setUpdatedFields] = useState([]);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [baaSigned, setBaaSigned] = useState(false);
  const [paSigned, setPaSigned] = useState(false);

  const [facilityName, setFacilityName] = useState("");
  const [facilityAddress, setFacilityAddress] = useState({});
  const [shippingContact, setShippingContact] = useState("");
  const [selectedFacility, setSelectedFacility] = useState("");
  const [primaryContactName, setPrimaryContactName] = useState("");
  const [primaryContactEmail, setPrimaryContactEmail] = useState("");
  const [primaryContactPhone, setPrimaryContactPhone] = useState("");
  const [shippingAddresses, setShippingAddresses] = useState([]);
  const [salesRepresentative, setSalesRepresentative] = useState("");
  const [physicianInfo, setPhysicianInfo] = useState({});
  const [additionalPhysicians, setAdditionalPhysicians] = useState([]);

  const [posType, setPosType] = useState(""); // Add this state for Place of Service
  const [showSuccess, setShowSuccess] = useState(false);

  const fillWithTestData = () => {
    setFirstName("John");
    setLastName("Smith");
    setEmail("testuser@example.com");
    setBaaSigned(true);
    setPaSigned(true);
    setFacilityName("Test Facility");
    setFacilityAddress({
        street: "123 Test St",
        city: "Testville",
        state: "CA",
        zip: "90001",
        npi: "1234567890",
        fax: "555-123-4567",
        // tin and ptan intentionally left blank
    });
    setShippingContact("Jane Doe");
    setSelectedFacility("Test Facility");
    setPrimaryContactName("Alice Johnson"); // <-- Set to physician's name or any test name
    setPrimaryContactEmail("alice.johnson@example.com"); // <-- Set to physician's email or any test email
    setPrimaryContactPhone("555-987-6543"); // <-- Set to physician's phone or any test phone
    setShippingAddresses([
        { street: "123 Test St", city: "Testville", state: "CA", zip: "90001" },
        { street: "456 Example Ave", city: "Sample City", state: "NY", zip: "10001" },
    ]);
    setSalesRepresentative("Test Rep");
    setPhysicianInfo({
        physicianFirstName: "Alice",
        physicianLastName: "Johnson",
        specialty: "Dermatology",
        npi: "9876543210",
        street: "789 Clinic Rd",
        city: "Healthtown",
        state: "TX",
        zip: "73301",
        contactName: "Dr. Alice Johnson",
        fax: "555-987-6543",
        phone: "555-123-4567",
        // tin and ptan intentionally left blank
    });
    setAdditionalPhysicians([
        {
        firstName: "Bob",
        lastName: "Williams",
        specialty: "Surgery",
        npi: "1122334455",
        street: "321 Hospital Blvd",
        city: "Medic City",
        state: "FL",
        zip: "33101",
        // tin and ptan intentionally left blank
        }
    ]);
    setPosType("outpatient");
    };

  const trackChange = (fieldName, newValue) => {
    setUpdatedFields((prev) =>
      !prev.includes(fieldName) ? [...prev, fieldName] : prev
    );
  };

  const handleCreate = async () => {
    setLoading(true);
    try {
      const token = getIdToken();

      const formData = {
        firstName,
        lastName,
        email,
        baaSigned,
        paSigned,
        facilityName,
        facilityAddress,
        facilityNPI: facilityAddress.npi || "",
        facilityTIN: facilityAddress.tin || "", // optional
        facilityPTAN: facilityAddress.ptan || "", // optional
        shippingContact,
        selectedFacility,
        primaryContactName,
        primaryContactEmail,
        primaryContactPhone,
        shippingAddresses,
        salesRepresentative,
        physicianInfo,
        additionalPhysicians,
        posType,
      };

      const res = await axios.post(
        "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/createUser",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (res.data.success) {
        toast({ title: "User created", status: "success", duration: 4000, isClosable: true });
        setCreatedUserId(res.data.userId);
        setUpdatedFields([]);
        setShowSuccess(true);
      } else {
        throw new Error("Creation failed");
      }
    } catch (err) {
      console.error("Error creating user:", err);
      setUpdateError(err.message || "Unknown error");
      toast({ title: "Failed to create user", status: "error", duration: 5000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyUserId = () => {
    if (createdUserId) {
      navigator.clipboard.writeText(createdUserId);
      toast({
        title: "Copied!",
        description: "User ID copied to clipboard.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setBaaSigned(false);
    setPaSigned(false);
    setFacilityName("");
    setFacilityAddress({});
    setShippingContact("");
    setSelectedFacility("");
    setPrimaryContactName("");
    setPrimaryContactEmail("");
    setPrimaryContactPhone("");
    setShippingAddresses([]);
    setSalesRepresentative("");
    setPhysicianInfo({});
    setAdditionalPhysicians([]);
    setPosType("");
    setCreatedUserId(null);
    setUpdatedFields([]);
    setUpdateError(null);
    setShowSuccess(false);
  };

  const isFormComplete =
    firstName &&
    lastName &&
    email &&
    facilityName &&
    facilityAddress.street &&
    facilityAddress.city &&
    facilityAddress.state &&
    facilityAddress.zip &&
    shippingContact &&
    primaryContactName &&
    primaryContactEmail &&
    primaryContactPhone;

    if (showSuccess && createdUserId) {
        return (
        <Box p={8} maxW="500px" mx="auto" mt={20} textAlign="center" borderWidth="1px" borderRadius="xl">
            <Heading color="green.500" mb={4}>✅ User Created Successfully</Heading>
            <Text fontSize="lg" mb={2} display="flex" alignItems="center" justifyContent="center" gap={2}>
            User ID: <strong>{createdUserId}</strong>
            <IconButton
                aria-label="Copy User ID"
                icon={<CopyIcon />}
                size="sm"
                onClick={handleCopyUserId}
                variant="ghost"
            />
            </Text>
            <Text fontSize="md" mb={2}>
              Name: <strong>{firstName} {lastName}</strong>
            </Text>
            <Text fontSize="md" mb={2}>
              Email: <strong>{email}</strong>
            </Text>
            <Text fontSize="md" mb={6}>
              Facility: <strong>{facilityName}</strong>
            </Text>
            <Button colorScheme="teal" onClick={resetForm}>
              Create Another User
            </Button>
        </Box>
        );
    }

  return (
    <Box p={6}>
      <Heading size="lg" mb={6}>
        Admin: Create New User with BAA
      </Heading>

      <Button colorScheme="purple" mb={4} onClick={fillWithTestData}>
        Fill with Test Data
      </Button>

      {/* First Name and Last Name Inputs */}
      <Flex mb={4} gap={4} justify="center">
        <Box display="flex" flexDirection="column" alignItems="center">
          <Text fontWeight="medium" mb={2} textAlign="center">First Name</Text>
          <Input
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            placeholder="Enter first name"
            maxW="200px"
            textAlign="center"
          />
        </Box>
        <Box display="flex" flexDirection="column" alignItems="center">
          <Text fontWeight="medium" mb={2} textAlign="center">Last Name</Text>
          <Input
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            placeholder="Enter last name"
            maxW="200px"
            textAlign="center"
          />
        </Box>
      </Flex>

      {/* Email Input */}
      <Box mb={4} display="flex" flexDirection="column" alignItems="center">
        <Text fontWeight="medium" mb={2} textAlign="center">User Email Address</Text>
        <Input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="Enter user email"
          maxW="350px"
          textAlign="center"
        />
      </Box>

      {/* BAA Signed Radio */}
        <Box mb={4} display="flex" flexDirection="column" alignItems="center">
        <Text fontWeight="medium" mb={2} textAlign="center">BAA Signed?</Text>
        <RadioGroup
            value={baaSigned ? "yes" : "no"}
            onChange={v => setBaaSigned(v === "yes")}
        >
            <Stack direction="row" justify="center">
            <Radio value="yes">Yes</Radio>
            <Radio value="no">No</Radio>
            </Stack>
        </RadioGroup>
        </Box>

        {/* PA Signed Radio */}
        <Box mb={4} display="flex" flexDirection="column" alignItems="center">
        <Text fontWeight="medium" mb={2} textAlign="center">PA Signed?</Text>
        <RadioGroup
            value={paSigned ? "yes" : "no"}
            onChange={v => setPaSigned(v === "yes")}
        >
            <Stack direction="row" justify="center">
            <Radio value="yes">Yes</Radio>
            <Radio value="no">No</Radio>
            </Stack>
        </RadioGroup>
        </Box>

      <Button onClick={onOpen}>Lookup NPI</Button>
      <NpiLookupDrawer isOpen={isOpen} onClose={onClose} />

      <BAAForm
        facilityName={facilityName}
        setFacilityName={(v) => {
          trackChange("Facility Name", v);
          setFacilityName(v);
        }}
        facilityAddress={facilityAddress}
        setFacilityAddress={(v) => {
          trackChange("Facility Address", v);
          setFacilityAddress(v);
        }}
        shippingContact={shippingContact}
        setShippingContact={(v) => {
          trackChange("Shipping Contact", v);
          setShippingContact(v);
        }}
        selectedFacility={selectedFacility}
        setSelectedFacility={(v) => {
          trackChange("Selected Facility", v);
          setSelectedFacility(v);
        }}
        primaryContactName={primaryContactName}
        setPrimaryContactName={(v) => {
          trackChange("Primary Contact Name", v);
          setPrimaryContactName(v);
        }}
        primaryContactEmail={primaryContactEmail}
        setPrimaryContactEmail={(v) => {
          trackChange("Primary Contact Email", v);
          setPrimaryContactEmail(v);
        }}
        primaryContactPhone={primaryContactPhone}
        setPrimaryContactPhone={(v) => {
          trackChange("Primary Contact Phone", v);
          setPrimaryContactPhone(v);
        }}
        shippingAddresses={shippingAddresses}
        setShippingAddresses={(v) => {
          trackChange("Shipping Addresses", v);
          setShippingAddresses(v);
        }}
        addShippingAddress={() => {
          const updated = [...shippingAddresses, { street: "", city: "", state: "", zip: "" }];
          setShippingAddresses(updated);
          trackChange("Shipping Addresses", updated);
        }}
        updateShippingAddress={(i, key, val) => {
          const updated = shippingAddresses.map((a, idx) => (i === idx ? { ...a, [key]: val } : a));
          setShippingAddresses(updated);
          trackChange("Shipping Addresses", updated);
        }}
        deleteShippingAddress={(i) => {
          const updated = shippingAddresses.filter((_, idx) => idx !== i);
          setShippingAddresses(updated);
          trackChange("Shipping Addresses", updated);
        }}
        salesRepresentative={salesRepresentative}
        setSalesRepresentative={(v) => {
          trackChange("Sales Representative", v);
          setSalesRepresentative(v);
        }}
        physicianInfo={physicianInfo}
        setPhysicianInfo={(v) => {
          trackChange("Physician Info", v);
          setPhysicianInfo(v);
        }}
        additionalPhysicians={additionalPhysicians}
        addPhysician={() => {
          const updated = [...additionalPhysicians, { firstName: "", lastName: "", specialty: "", npi: "", tin: "", ptan: "", street: "", city: "", state: "", zip: "" }];
          setAdditionalPhysicians(updated);
          trackChange("Additional Physicians", updated);
        }}
        updatePhysician={(index, field, value) => {
          const updated = additionalPhysicians.map((p, i) => (i === index ? { ...p, [field]: value } : p));
          setAdditionalPhysicians(updated);
          trackChange("Additional Physicians", updated);
        }}
        deletePhysician={(index) => {
          const updated = additionalPhysicians.filter((_, i) => i !== index);
          setAdditionalPhysicians(updated);
          trackChange("Additional Physicians", updated);
        }}
        handleSameAsAbove={(index) => {
          const updated = [...additionalPhysicians];
          updated[index] = {
            ...updated[index],
            street: physicianInfo.street,
            city: physicianInfo.city,
            state: physicianInfo.state,
            zip: physicianInfo.zip,
          };
          setAdditionalPhysicians(updated);
          trackChange("Additional Physicians", updated);
        }}
        handleNext={handleCreate}
        isSubmitting={loading}
        updateError={updateError}
        isFormComplete={isFormComplete}
      />

      {updatedFields.length > 0 && (
        <Box mt={8} bg="gray.50" p={4} borderRadius="md">
          <Heading size="sm" mb={2}>Fields Filled:</Heading>
          <UnorderedList>
            {updatedFields.map((field) => (
              <ListItem key={field}>{field}</ListItem>
            ))}
          </UnorderedList>
        </Box>
      )}
    </Box>
  );
};

export default AdminBAACreator;